// Pure sanitizers shared by the browser (before sending to CloudWatch RUM or
// the client error route) and the server (which re-applies them instead of
// trusting the browser). Every function is idempotent on its own output.

export const CLIENT_ERROR_MESSAGE_MAX_LENGTH = 200;
export const CLIENT_ERROR_FRAME_MAX_LENGTH = 300;
export const CLIENT_ERROR_FILENAME_MAX_LENGTH = 200;
export const CLIENT_ERROR_POSITION_MAX = 999_999_999;
export const CLIENT_ERROR_REPORTED_FRAME_LIMIT = 5;
export const CLIENT_ERROR_CUSTOM_MESSAGE = "<custom>";
export const CLIENT_ERROR_FILENAME_CATEGORIES = [
  "empty",
  "extension",
  "cross_origin",
  "same_origin_other",
] as const;
export const PRINTABLE_ASCII = /^[\x20-\x7E]+$/;

const MAX_RAW_MESSAGE_LENGTH = 2_000;
// Only enumerated error names may prefix a template; a custom name such as
// "CustomerError: " makes the whole message custom.
const MESSAGE_PREFIX =
  /^(?:Uncaught (?:\(in promise\) )?)?(?:(?:Type|Reference|Range|Syntax|Eval|URI|ChunkLoad|Abort|NotAllowed|QuotaExceeded|Network|InvalidState|Security)?Error: )?/;

// Fixed messages emitted by V8, SpiderMonkey, and JavaScriptCore.
const FIXED_MESSAGES = new Set([
  "Failed to fetch",
  "Load failed",
  "NetworkError when attempting to fetch resource.",
  "Importing a module script failed.",
  "Maximum call stack size exceeded",
  "too much recursion",
  "Unexpected end of JSON input",
  "Script error.",
  "ResizeObserver loop completed with undelivered notifications.",
  "ResizeObserver loop limit exceeded",
  "The user aborted a request.",
  "The operation was aborted.",
  "This operation was aborted",
  "signal is aborted without reason",
  "Fetch is aborted",
  "BodyStreamBuffer was aborted",
  "The request is not allowed by the user agent or the platform in the current context, possibly because the user denied permission.",
  "play() failed because the user didn't interact with the document first.",
  "Permission denied",
  "The quota has been exceeded.",
  CLIENT_ERROR_CUSTOM_MESSAGE,
]);

// Browser message shapes. Every variable part is replaced by a placeholder;
// only enumerated alternatives (undefined/null, reading/setting, React error
// codes) are copied. Specific shapes precede the generic `... is not ...`.
const MESSAGE_SHAPES: [RegExp, string][] = [
  [
    /^Cannot (read|set) properties of (undefined|null) \((reading|setting) .+\)$/,
    "Cannot $1 properties of $2 ($3 <name>)",
  ],
  [
    /^Cannot (read|set) property .+ of (undefined|null)$/,
    "Cannot $1 property <name> of $2",
  ],
  [
    /^(undefined|null) is not an object \(evaluating .+\)$/,
    "$1 is not an object (evaluating <expr>)",
  ],
  [
    /^can't access property .+, .+ is (undefined|null)$/,
    "can't access property <name>, <expr> is $1",
  ],
  [/^Can't find variable: .+$/, "Can't find variable: <name>"],
  [/^Failed to fetch \(.+\)$/, "Failed to fetch"],
  [
    /^Failed to fetch dynamically imported module: .+$/,
    "Failed to fetch dynamically imported module: <url>",
  ],
  [
    /^error loading dynamically imported module(?:: .+)?$/,
    "error loading dynamically imported module: <url>",
  ],
  [/^Loading (CSS )?chunk .+ failed\..*$/, "Loading $1chunk <id> failed."],
  [/^Failed to load chunk .+$/, "Failed to load chunk <url>"],
  [
    /^Unexpected token .+, .+ is not valid JSON$/,
    "Unexpected token <token>, <text> is not valid JSON",
  ],
  [
    /^Unexpected token .+ in JSON at position \d+$/,
    "Unexpected token <token> in JSON at position <n>",
  ],
  [/^.+ is not valid JSON$/, "<text> is not valid JSON"],
  [/^JSON\.parse: .+$/, "JSON.parse: <detail>"],
  [/^JSON Parse error: .+$/, "JSON Parse error: <detail>"],
  [/^Minified React error #(\d{1,4})\b.*$/, "Minified React error #$1"],
  [/^Hydration failed\b.*$/, "Hydration failed <detail>"],
  [
    /^Failed to execute 'setItem' on 'Storage': Setting the value of .+ exceeded the quota\.$/,
    "Failed to execute 'setItem' on 'Storage': Setting the value of <name> exceeded the quota.",
  ],
  [/^.+ is not defined$/, "<name> is not defined"],
  [/^.+ is not a function\b.*$/, "<expr> is not a function"],
  [/^.+ is not a constructor\b.*$/, "<expr> is not a constructor"],
  [/^.+ is not iterable\b.*$/, "<expr> is not iterable"],
];

const EXTENSION_URL =
  /^(?:chrome|moz|safari|safari-web|ms-browser)-extension:|^webkit-masked-url:/i;
const OWN_STATIC_SCRIPT_PATH = /^\/_next\/static\/[A-Za-z0-9_./-]+\.js$/;
const RELATIVE_BASE = "https://client-error.invalid";

const SAFE_STACK_LOCATION =
  /(?:https?:\/\/[A-Za-z0-9.:-]+)?(\/_next\/static\/[A-Za-z0-9_./-]+\.js):([1-9]\d{0,8}):([1-9]\d{0,8})/;

/**
 * Maps a browser or runtime error message to an allowlisted template whose
 * variable parts (names, expressions, URLs, tokens) are placeholders, so no
 * free text survives. Any other non-empty message becomes `<custom>`.
 */
export function toClientErrorMessageTemplate(message: unknown) {
  if (typeof message !== "string") return undefined;
  const normalized = message
    .slice(0, MAX_RAW_MESSAGE_LENGTH)
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return undefined;
  const prefix = normalized.match(MESSAGE_PREFIX)?.[0] ?? "";
  const body = normalized.slice(prefix.length);
  if (FIXED_MESSAGES.has(body)) {
    return body === CLIENT_ERROR_CUSTOM_MESSAGE ? body : `${prefix}${body}`;
  }
  for (const [shape, template] of MESSAGE_SHAPES) {
    if (shape.test(body)) return `${prefix}${body.replace(shape, template)}`;
  }
  return CLIENT_ERROR_CUSTOM_MESSAGE;
}

/**
 * Keeps the path of the application's own `_next/static` scripts and collapses
 * every other script location to a category. Without an origin (on the server)
 * only already-relative paths count as the application's own.
 */
export function toClientErrorFilename(filename: unknown, origin?: string) {
  const value = typeof filename === "string" ? filename.trim() : "";
  if (!value) return "empty";
  if ((CLIENT_ERROR_FILENAME_CATEGORIES as readonly string[]).includes(value)) {
    return value;
  }
  if (EXTENSION_URL.test(value)) return "extension";

  let url: URL;
  try {
    url = new URL(value, origin ?? RELATIVE_BASE);
  } catch {
    return "cross_origin";
  }
  if (url.origin !== new URL(origin ?? RELATIVE_BASE).origin) {
    return "cross_origin";
  }
  return OWN_STATIC_SCRIPT_PATH.test(url.pathname) &&
    !url.pathname.includes("..")
    ? url.pathname
    : "same_origin_other";
}

/** A line or column number when it is a plausible source position. */
export function toClientErrorPosition(value: unknown) {
  return typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 0 &&
    value <= CLIENT_ERROR_POSITION_MAX
    ? value
    : undefined;
}

/**
 * Reduces a V8 or Firefox/Safari stack line to its `/_next/static/...js:line:
 * column` location; any other line is dropped. Function names are discarded:
 * production names are minified, can be built from runtime data (for example
 * `Object.<key>`), and RUM restores source maps from the location.
 */
export function sanitizeClientErrorStackFrame(line: string) {
  const normalized = line.trim();
  if (
    normalized.length > MAX_RAW_MESSAGE_LENGTH ||
    /[?#]/.test(normalized) ||
    /\beval\b/i.test(normalized)
  ) {
    return undefined;
  }

  const location = normalized.match(SAFE_STACK_LOCATION);
  if (!location || location.index === undefined) return undefined;
  const [matchedLocation, path, lineNumber, columnNumber] = location;
  if (!path || path.includes("..") || !lineNumber || !columnNumber) {
    return undefined;
  }

  const before = normalized.slice(0, location.index);
  const after = normalized.slice(location.index + matchedLocation.length);
  const isFrame =
    before === "" || before.startsWith("at ") || before.endsWith("@");
  if (!isFrame || (after !== "" && after !== ")")) return undefined;
  return `${path}:${lineNumber}:${columnNumber}`;
}
